'use client'

import { useAtom, useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'

import {
  EMPTY_GUIDE_PREFERENCES,
  type GuidePreferences,
  GUIDES_COMPONENT_ID,
  mergeGuidePreferences,
  parseGuidePreferences,
  serializeGuidePreferences,
} from '@/features/onboarding/lib/guide-preferences'
import { getComponentPreference, saveComponentPreference } from '@/service/ui-state.api'
import { guidePreferencesReadyAtom, welcomeGuideAtom } from '@/state/onboarding'
import { orderTypeGuideSeenAtom } from '@/state/order-type-guide'
import { presetTourDoneAtom } from '@/state/preset-tour'
import { tutorialProgressAtom } from '@/state/tutorial'

const SAVE_DEBOUNCE_MS = 800

// Module scope, like use-page-tabs-sync: once per user session, not once per mount.
let hydratedUserKey: string | null = null
let saveArmedUserKey: string | null = null
let lastSyncedSnapshot: string | null = null

/** Called from the logout reset path, alongside the atoms it wipes. */
export function resetGuidesSyncState() {
  hydratedUserKey = null
  saveArmedUserKey = null
  lastSyncedSnapshot = null
}

/**
 * Keeps the user's guide state — order type animations seen, welcome and preset tours done,
 * tutorial progress — in the backend's per-user component-preference store, so it follows the user to
 * any page and any device. On load the server copy is merged into the local one (nothing is
 * lost either way: see `mergeGuidePreferences`); changes are pushed up debounced.
 *
 * Saves replace the whole record, so they only start once the server copy has been read:
 * if that fails, this session keeps its changes locally rather than overwrite what other
 * devices saved.
 */
export function useGuidesSync({
  isLoggedIn,
  userId,
}: {
  isLoggedIn: boolean
  userId?: string | null
}) {
  const [seenByUser, setSeenByUser] = useAtom(orderTypeGuideSeenAtom)
  const [welcomeGuide, setWelcomeGuide] = useAtom(welcomeGuideAtom)
  const [tutorialProgress, setTutorialProgress] = useAtom(tutorialProgressAtom)
  const [presetTour, setPresetTour] = useAtom(presetTourDoneAtom)
  const setReady = useSetAtom(guidePreferencesReadyAtom)
  // Re-runs the save effect once hydration arms it, even if merging changed nothing locally.
  const [armedFor, setArmedFor] = useState<string | null>(null)

  const orderTypeGuide = userId ? seenByUser[userId] : undefined
  const local = useMemo<GuidePreferences>(
    () => ({ orderTypeGuide: orderTypeGuide ?? {}, welcomeGuide, tutorialProgress, presetTour }),
    [orderTypeGuide, welcomeGuide, tutorialProgress, presetTour]
  )

  useEffect(() => {
    if (!isLoggedIn || !userId || hydratedUserKey === userId) return
    hydratedUserKey = userId
    saveArmedUserKey = null
    lastSyncedSnapshot = null

    getComponentPreference(GUIDES_COMPONENT_ID)
      .then((pref) => {
        const server = parseGuidePreferences(pref?.config)
        lastSyncedSnapshot = JSON.stringify(serializeGuidePreferences(server))
        // Into whatever is local *now*, so a guide dismissed while this was in flight stays.
        const merged = (part: Partial<GuidePreferences>) =>
          mergeGuidePreferences({ ...EMPTY_GUIDE_PREFERENCES, ...part }, server)
        setSeenByUser((prev) => {
          const next = merged({ orderTypeGuide: prev[userId] ?? {} }).orderTypeGuide
          return sameJson(next, prev[userId] ?? {}) ? prev : { ...prev, [userId]: next }
        })
        setWelcomeGuide((prev) => {
          const next = merged({ welcomeGuide: prev }).welcomeGuide
          return sameJson(next, prev) ? prev : next
        })
        setTutorialProgress((prev) => {
          const next = merged({ tutorialProgress: prev }).tutorialProgress
          return sameJson(next, prev) ? prev : next
        })
        setPresetTour((prev) => prev || server.presetTour)
        saveArmedUserKey = userId
        setArmedFor(userId)
      })
      .catch((err) => {
        hydratedUserKey = null // retry on the next mount
        console.warn('[guidesSync] failed to load guide preferences', err)
      })
      .finally(() => setReady(true))
  }, [
    isLoggedIn,
    userId,
    setSeenByUser,
    setWelcomeGuide,
    setTutorialProgress,
    setPresetTour,
    setReady,
  ])

  useEffect(() => {
    if (!isLoggedIn || !userId || saveArmedUserKey !== userId || armedFor !== userId) return
    const config = serializeGuidePreferences(local)
    const serialized = JSON.stringify(config)
    if (serialized === lastSyncedSnapshot) return

    const timer = setTimeout(() => {
      lastSyncedSnapshot = serialized
      saveComponentPreference({ componentId: GUIDES_COMPONENT_ID, columnOrder: [], config }).catch(
        (err) => {
          lastSyncedSnapshot = null // let the next change retry the upload
          console.warn('[guidesSync] failed to save guide preferences', err)
        }
      )
    }, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [isLoggedIn, userId, armedFor, local])
}

function sameJson(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b)
}
